import { useEffect } from "react";

const SITE = "Sybils & Society";
const DEFAULT_IMG = "/sybils-logo.png";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (data) {
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
  } else if (el) {
    el.remove();
  }
}

// Absolute-ize an image URL against the current origin so social crawlers get a full URL.
function absUrl(src) {
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  return (typeof window !== "undefined" ? window.location.origin : "") + (src.startsWith("/") ? src : `/${src}`);
}

export const Seo = ({
  title,
  description,
  image = DEFAULT_IMG,
  type = "website",
  article, // { author, category, publishedTime } for posts
}) => {
  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE}` : `${SITE} — A Cultural & Esoteric Review`;
    const url = typeof window !== "undefined" ? window.location.href : "https://sybilssociety.com/";
    const img = absUrl(image);

    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertLink("canonical", url);

    // Open Graph
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:site_name", SITE);

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    // Article JSON-LD for posts
    if (type === "article" && article) {
      setJsonLd("seo-article-jsonld", {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        description,
        image: [img],
        author: { "@type": "Person", name: article.author || "Veronica Bey" },
        publisher: {
          "@type": "Organization",
          name: SITE,
          logo: { "@type": "ImageObject", url: absUrl(DEFAULT_IMG) },
        },
        articleSection: article.category,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        ...(article.publishedTime ? { datePublished: article.publishedTime } : {}),
      });
    } else {
      setJsonLd("seo-article-jsonld", null);
    }

    return () => setJsonLd("seo-article-jsonld", null);
  }, [title, description, image, type, article]);

  return null;
};

export default Seo;
